<template>
  <div style="display: flex; height: 100%">
    <!-- Left pane: recommendation list -->
    <div
      style="
        margin-top: 0px;
        overflow-y: auto;
        height: 100%;
        overflow-x: hidden;
        padding-right: 8px;
        width: 100%;
      "
    >
      <sh-wrapper direction="row" spacing="s" style="margin-bottom: 5px">
        <sh-icon
          icon="refresh"
          size="m"
          id="shift-refresh-icon"
          button
          @click="loadRecommendations(true)"
        ></sh-icon>
        <sh-tooltip
          label="Refresh"
          target="shift-refresh-icon"
          placement="bottom-right"
          variation="short"
        ></sh-tooltip>
        <sh-spinner
          size="s"
          v-if="loadingShiftRecommendations"
          label="Loading shift recommendations"
        ></sh-spinner>
      </sh-wrapper>

      <sh-text size="super-header">Shift Recommendations</sh-text>
      <sh-text
        v-if="!loadingShiftRecommendations && shiftRecommendations.length === 0"
        >No shift recommendations available</sh-text
      >

      <sh-menu-item
        v-for="(item, index) in shiftRecommendations"
        :key="'shiftrecmenu' + index"
        label=""
        @expand="blockEvent"
        @collapse="blockEvent"
        expanded="true"
      >
        <div slot="functions" style="margin-left: -100%; width: 100%">
          <div
            style="display: inline-flex; align-items: center; gap: 8px; cursor: pointer; flex-wrap: nowrap; white-space: nowrap; width: 100%"
            @click.stop="openShiftReport(item)"
          >
            <sh-text
              size="header-1"
              style="color: #ec6602; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; max-width: 75%"
            >
              {{ item.recommendation_id }}
            </sh-text>
            <span
              :style="{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 600,
                color: hasShiftRecommendationFeedback(item.recommendation_id)
                  ? '#1a7f37'
                  : '#b36b00',
                backgroundColor: hasShiftRecommendationFeedback(item.recommendation_id)
                  ? 'rgba(26, 127, 55, 0.12)'
                  : 'rgba(179, 107, 0, 0.12)',
                flexShrink: 0,
              }"
            >
              {{ hasShiftRecommendationFeedback(item.recommendation_id) ? 'Interacted' : 'New' }}
            </span>
          </div>
        </div>
        <div style="display: flex; text-align: left; margin-left: -26px">
          <div>
            <sh-text v-if="item.generated_at">Generated: {{ formatDate(item.generated_at) }}</sh-text>
            <sh-text v-if="hasShiftRecommendationFeedback(item.recommendation_id)">Feedback: {{ formatDate(shiftRecommendationsFeedbackDates[item.recommendation_id]) }}</sh-text>
            <sh-text v-else>Feedback: Not provided</sh-text>
            <sh-text v-if="item.area_id">Plant section: {{ item.area_id }}</sh-text>
          </div>
        </div>
      </sh-menu-item>
    </div>

    <!-- Modal: shift report + feedback -->
    <teleport to="body">
      <sh-modal
        label="Shift Recommendation Report"
        id="shift-report-modal"
        modal-height="900px"
        icon="document"
        modal-width="2050px"
        style="z-index: 10000"
        @modal-closed="closeShiftReportModal"
      >
      <div style="margin-bottom: 8px">
        <sh-text size="title-1" v-if="selectedShiftRecommendation">
          Recommendation: {{ selectedShiftRecommendation.recommendation_id }}
        </sh-text>
      </div>

      <sh-spinner
        size="s"
        v-if="loadingShiftReport"
        label="Loading report"
      ></sh-spinner>

      <div
        v-if="!loadingShiftReport"
        style="display: flex; gap: 20px; height: 800px; flex-wrap: nowrap"
      >
        <div style="flex: 1 1 1260px; min-width: 0; border: 1px solid #d9d9d9">
          <iframe
            v-if="selectedShiftReportHtml"
            :srcdoc="selectedShiftReportHtml"
            title="Shift Recommendation Report"
            style="width: 100%; height: 100%; border: none"
          ></iframe>
          <div
            v-else
            style="display: flex; align-items: center; justify-content: center; height: 100%"
          >
            <sh-text>Shift recommendation report unavailable</sh-text>
          </div>
        </div>

        <div
          style="flex: 0 0 450px; max-width: 450px; overflow-y: auto; padding-left: 4px"
        >
          <sh-text size="header-1">Feedback</sh-text>

          <div style="margin-top: 12px">
            <sh-text size="body-1" style="font-weight: 600"
              >Coverage And Workload Gap</sh-text
            >
            <sh-text style="margin-top: 6px; display: block"
              >Did this recommendation rationale identify a real coverage or workload gap in your region?</sh-text
            >
            <div style="margin-top: 6px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px">
              <sh-button
                v-for="option in ['Yes', 'No', 'Maybe']"
                :key="'identifiesCoverageGap-' + option"
                :label="option"
                :color="shiftFeedback.identifiesCoverageGap === option ? 'primary' : 'tertiary'"
                @click="setShiftFeedbackAnswer('identifiesCoverageGap', option)"
                style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
              ></sh-button>
            </div>
          </div>

          <div style="margin-top: 16px">
            <sh-text size="body-1" style="font-weight: 600"
              >Recommendation Quality</sh-text
            >
            <sh-text style="margin-top: 6px; display: block"
              >Did the given recommendation address the gap provided?</sh-text
            >
            <div style="margin-top: 6px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px">
              <sh-button
                v-for="option in ['Yes', 'No', 'Maybe']"
                :key="'recommendationAddressesGap-' + option"
                :label="option"
                :color="shiftFeedback.recommendationAddressesGap === option ? 'primary' : 'tertiary'"
                @click="setShiftFeedbackAnswer('recommendationAddressesGap', option)"
                style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
              ></sh-button>
            </div>
          </div>

          <div style="margin-top: 16px">
            <sh-text size="body-1" style="font-weight: 600"
              >Operational Feasibility</sh-text
            >
            <sh-text style="margin-top: 6px; display: block"
              >Will the proposed shift change be implemented operationally?</sh-text
            >
            <div style="margin-top: 6px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px">
              <sh-button
                v-for="option in ['Yes', 'No', 'Maybe']"
                :key="'recommendationImplPlanned-' + option"
                :label="option"
                :color="shiftFeedback.recommendationImplPlanned === option ? 'primary' : 'tertiary'"
                @click="setShiftFeedbackAnswer('recommendationImplPlanned', option)"
                style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
              ></sh-button>
            </div>
          </div>

          <div style="margin-top: 16px">
            <sh-text size="body-1" style="font-weight: 600"
              >Data Completeness</sh-text
            >
            <sh-text style="margin-top: 6px; display: block"
              >Any other data elements you would like to see in this report?</sh-text
            >
            <textarea
              :value="shiftFeedback.additionalDataElementsRequired"
              @input="setShiftFeedbackText('additionalDataElementsRequired', $event)"
              rows="5"
              placeholder="Additional data elements"
              style="
                margin-top: 6px;
                width: 100%;
                resize: none;
                padding: 8px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                font-size: 14px;
                font-family: inherit;
                box-sizing: border-box;
              "
            ></textarea>
          </div>

          <div style="margin-top: 16px">
            <sh-text size="body-1" style="font-weight: 600"
              >Overall Feedback</sh-text
            >
            <textarea
              :value="shiftFeedback.overallComment"
              @input="setShiftFeedbackText('overallComment', $event)"
              rows="5"
              placeholder="Overall feedback"
              style="
                margin-top: 6px;
                width: 100%;
                resize: none;
                padding: 8px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
                font-size: 14px;
                font-family: inherit;
                box-sizing: border-box;
              "
            ></textarea>
          </div>

          <div style="margin-top: 20px; margin-bottom: 8px">
            <button
              @click="submitShiftFeedback"
              :disabled="loadingShiftFeedback"
              style="
                width: 100%;
                padding: 8px 16px;
                background-color: #ec6602;
                color: #fff;
                border: none;
                border-radius: 4px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                white-space: nowrap;
              "
            >{{ loadingShiftFeedback ? "Submitting..." : "Submit Feedback" }}</button>
          </div>
        </div>
      </div>
      </sh-modal>
    </teleport>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";

export default {
  name: "ShiftRecommendations",
  props: {
    area: { type: String, required: true },
    selecteddivision: { type: String, required: true },
    selectedCountry: { type: Object, required: true },
    backendURL: { type: String, required: true },
  },
  data() {
    return {
      shiftRecommendations: [],
      loadingShiftRecommendations: false,
      loadingShiftReport: false,
      loadingShiftFeedback: false,
      selectedShiftRecommendation: null,
      shiftRecommendationsFeedbackDates: {},
      selectedShiftReportHtml: "",
      shiftFeedback: {
        identifiesCoverageGap: "",
        recommendationAddressesGap: "",
        recommendationImplPlanned: "",
        additionalDataElementsRequired: "",
        overallComment: "",
      },
    };
  },
  computed: {
    ...mapGetters("oidcStore", ["oidcUser"]),
  },
  mounted() {
    this.tryLoadRecommendations();
  },
  watch: {
    area() {
      this.tryLoadRecommendations();
    },
    selecteddivision() {
      this.tryLoadRecommendations();
    },
    selectedCountry: {
      handler() {
        this.tryLoadRecommendations();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(["pushNotification"]),

    tryLoadRecommendations() {
      if (!this.area || !this.selecteddivision) {
        return;
      }

      if (
        !this.selectedCountry ||
        !this.selectedCountry.iso_3166_1 ||
        String(this.selectedCountry.iso_3166_1).trim() === ""
      ) {
        return;
      }

      this.loadRecommendations();
    },

    blockEvent() {
      return;
    },

    loadRecommendations(forceFeedbackRefresh = false) {
      this.getShiftRecommendations({
        area: this.area,
        selecteddivision: this.selecteddivision,
        forceFeedbackRefresh,
      });
    },

    async getShiftRecommendations(params) {
      this.loadingShiftRecommendations = true;
      const forceFeedbackRefresh = Boolean(params && params.forceFeedbackRefresh);

      let reqStr = "";
      if (params.selecteddivision === "ImagingZone") {
        reqStr =
          this.backendURL +
          "/api/service_zone_current_data/shift_recommendations_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          params.area;
      } else {
        reqStr =
          this.backendURL +
          "/api/service_current_data/shift_recommendations_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          params.area;
      }

      try {
        const res = await axios.get(reqStr);
        const recommendations =
          res && res.data && res.data.recommendations
            ? res.data.recommendations
            : [];
        this.shiftRecommendations = recommendations
          .map((item) => {
            if (item && item.metadata && typeof item.metadata === "object") {
              return { ...item.metadata };
            }
            if (item && typeof item === "object") {
              return item;
            }
            return null;
          })
          .filter(
            (item) =>
              item &&
              typeof item === "object" &&
              Object.prototype.hasOwnProperty.call(item, "recommendation_id")
          );
      } catch (err) {
        console.log(err);
        this.shiftRecommendations = [];
        this.pushNotification({
          type: "warning",
          label: "No Shift Recommendations",
          duration: "long",
          text: "Shift recommendations unavailable",
        });
      } finally {
        this.loadingShiftRecommendations = false;
        if (this.shiftRecommendations.length > 0) {
          const recIds = this.shiftRecommendations.map((item) => item.recommendation_id);
          this.getBatchShiftFeedbackDates(recIds, forceFeedbackRefresh);
        }
      }
    },

    async getBatchShiftFeedbackDates(recommendationIds, forceFeedbackRefresh = false) {
      const promises = recommendationIds.map((recId) =>
        this.fetchSingleFeedbackDate(recId, forceFeedbackRefresh)
      );
      try {
        await Promise.all(promises);
      } catch (err) {
        console.log("Error fetching batch feedback dates:", err);
      }
    },

    async fetchSingleFeedbackDate(recommendationId, forceFeedbackRefresh = false) {
      if (
        !forceFeedbackRefresh &&
        this.shiftRecommendationsFeedbackDates[recommendationId] !== undefined
      ) {
        return;
      }

      let reqStr = "";
      if (this.selecteddivision === "ImagingZone") {
        reqStr =
          this.backendURL +
          "/api/service_zone_current_data/shift_feedback_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area +
          "/" +
          recommendationId;
      } else {
        reqStr =
          this.backendURL +
          "/api/service_current_data/shift_feedback_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area +
          "/" +
          recommendationId;
      }

      try {
        const res = await axios.get(reqStr);
        if (res && res.data && res.data.found && res.data.feedback_record && res.data.feedback_record.submitted_at) {
          this.shiftRecommendationsFeedbackDates[recommendationId] = res.data.feedback_record.submitted_at;
        } else {
          this.shiftRecommendationsFeedbackDates[recommendationId] = null;
        }
      } catch (err) {
        console.log("Error fetching feedback date for", recommendationId, err);
        this.shiftRecommendationsFeedbackDates[recommendationId] = null;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        const options = { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" };
        return date.toLocaleDateString("en-US", options);
      } catch (err) {
        console.log("Error formatting date:", err);
        return dateString;
      }
    },

    hasShiftRecommendationFeedback(recommendationId) {
      return Boolean(this.shiftRecommendationsFeedbackDates[recommendationId]);
    },

    openShiftReport(shiftRecommendation) {
      if (!shiftRecommendation || !("recommendation_id" in shiftRecommendation)) {
        return;
      }

      this.selectedShiftRecommendation = { ...shiftRecommendation };
      this.selectedShiftReportHtml = "";
      this.shiftFeedback = {
        identifiesCoverageGap: "",
        recommendationAddressesGap: "",
        recommendationImplPlanned: "",
        additionalDataElementsRequired: "",
        overallComment: "",
      };
      document.querySelector("#shift-report-modal").visible = true;
      this.getShiftRecommendationReport(shiftRecommendation.recommendation_id);
      this.getShiftFeedback(shiftRecommendation.recommendation_id);
    },

    async getShiftRecommendationReport(recommendationId) {
      this.loadingShiftReport = true;

      let reqStr = "";
      if (this.selecteddivision === "ImagingZone") {
        reqStr =
          this.backendURL +
          "/api/service_zone_current_data/shift_recommendation_report/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area +
          "/" +
          recommendationId;
      } else {
        reqStr =
          this.backendURL +
          "/api/service_current_data/shift_recommendation_report/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area +
          "/" +
          recommendationId;
      }

      try {
        const res = await axios.get(reqStr);
        this.selectedShiftReportHtml =
          res && res.data && res.data.html ? res.data.html : "";
      } catch (err) {
        console.log(err);
        this.selectedShiftReportHtml = "";
        this.pushNotification({
          type: "error",
          label: "Shift report unavailable",
          duration: "long",
          text: "Failed to load shift recommendation report",
        });
      } finally {
        this.loadingShiftReport = false;
      }
    },

    async getShiftFeedback(recommendationId) {
      let reqStr = "";
      if (this.selecteddivision === "ImagingZone") {
        reqStr =
          this.backendURL +
          "/api/service_zone_current_data/shift_feedback_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area +
          "/" +
          recommendationId;
      } else {
        reqStr =
          this.backendURL +
          "/api/service_current_data/shift_feedback_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area +
          "/" +
          recommendationId;
      }

      try {
        const res = await axios.get(reqStr);
        if (
          res &&
          res.data &&
          res.data.found &&
          res.data.feedback_record &&
          res.data.feedback_record.feedback
        ) {
          this.shiftFeedback = {
            ...this.shiftFeedback,
            ...res.data.feedback_record.feedback,
          };
        }
      } catch (err) {
        console.log(err);
      }
    },

    closeShiftReportModal() {
      document.querySelector("#shift-report-modal").visible = false;
      this.selectedShiftRecommendation = null;
      this.selectedShiftReportHtml = "";
      this.shiftFeedback = {
        identifiesCoverageGap: "",
        recommendationAddressesGap: "",
        recommendationImplPlanned: "",
        additionalDataElementsRequired: "",
        overallComment: "",
      };
      this.loadingShiftReport = false;
      this.loadingShiftFeedback = false;
    },

    setShiftFeedbackAnswer(field, value, active) {
      if (typeof active === "boolean" && !active) {
        return;
      }
      this.shiftFeedback[field] = String(value);
    },

    setShiftFeedbackText(field, event) {
      this.shiftFeedback[field] = event.target.value;
    },

    async submitShiftFeedback() {
      const requiredFields = [
        "identifiesCoverageGap",
        "recommendationAddressesGap",
        "recommendationImplPlanned",
        "additionalDataElementsRequired",
        "overallComment",
      ];

      const missingFields = requiredFields.filter((field) => {
        const value = this.shiftFeedback[field];
        return !value || String(value).trim() === "";
      });

      if (missingFields.length > 0) {
        this.pushNotification({
          type: "warning",
          label: "Incomplete Feedback",
          duration: "long",
          text: "Please complete all feedback fields before submitting.",
        });
        return;
      }

      let reqStr = "";
      if (this.selecteddivision === "ImagingZone") {
        reqStr =
          this.backendURL +
          "/api/service_zone_current_data/shift_feedback_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area;
      } else {
        reqStr =
          this.backendURL +
          "/api/service_current_data/shift_feedback_new/" +
          this.selectedCountry.iso_3166_1 +
          "/" +
          this.area;
      }

      const payload = {
        recommendation_id: this.selectedShiftRecommendation
          ? this.selectedShiftRecommendation.recommendation_id
          : "",
        submitted_at: new Date().toISOString(),
        submitted_by: this.oidcUser ? this.oidcUser.email : "",
        feedback: {
          ...this.shiftFeedback,
          overallComment: String(this.shiftFeedback.overallComment).trim(),
        },
      };

      this.loadingShiftFeedback = true;
      try {
        const res = await axios.post(reqStr, payload);
        if (
          res &&
          res.data &&
          res.data.feedback_record &&
          res.data.feedback_record.feedback
        ) {
          this.shiftFeedback = {
            ...this.shiftFeedback,
            ...res.data.feedback_record.feedback,
          };
        }
        this.pushNotification({
          type: "success",
          label: "Feedback Submitted",
          duration: "long",
          text: "Thank you. Your shift recommendation feedback has been recorded.",
        });
        this.closeShiftReportModal();
      } catch (err) {
        console.log(err);
        this.pushNotification({
          type: "error",
          label: "Feedback Submit Failed",
          duration: "long",
          text: "Unable to save shift feedback",
        });
      } finally {
        this.loadingShiftFeedback = false;
      }
    },
  },
};
</script>
